import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { medicationsHome } from '../../lib/roleHome';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useMedicationCart, CartItem } from '../../contexts/MedicationCartContext';

interface CartDrawerProps {
  open: boolean;
  onClose: () => void;
}

const CartItemRow: React.FC<{ item: CartItem; onRemove: () => void }> = ({ item, onRemove }) => (
  <Box
    sx={{
      background: '#F0F5FF',
      borderRadius: '10px',
      px: 1.5,
      py: 1.25,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#14171A', flex: 1, minWidth: 0 }}>
      {item.name}
    </Typography>
    <IconButton size="small" onClick={onRemove} sx={{ color: '#E53935', ml: 1 }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>
);

export const CartDrawer: React.FC<CartDrawerProps> = ({ open, onClose }) => {
  const { items, removeItem, clearCart, itemCount } = useMedicationCart();
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleReviewAndPrint = () => {
    onClose();
    navigate(`${medicationsHome(user?.role)}/print`);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '20px 20px 0 0',
          maxHeight: '70vh',
          px: 2.5,
          py: 2,
          paddingBottom: { xs: 'calc(16px + env(safe-area-inset-bottom))', md: 2 },
        },
      }}
    >
      {/* Drag handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.5 }}>
        <Box sx={{ width: 40, height: 4, background: '#E1E8ED', borderRadius: 2 }} />
      </Box>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography sx={{ fontWeight: 800, fontSize: 17, color: '#14171A' }}>
          Medicamentos ({itemCount})
        </Typography>
        {itemCount > 0 && (
          <Typography
            onClick={clearCart}
            sx={{ fontSize: 13, color: '#E53935', cursor: 'pointer', fontWeight: 600 }}
          >
            Limpiar
          </Typography>
        )}
      </Box>

      {/* Cart items */}
      {itemCount === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography sx={{ color: '#A0AEC0', fontSize: 14 }}>
            No hay medicamentos agregados
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, overflowY: 'auto', mb: 2 }}>
          {items.map((item) => (
            <CartItemRow
              key={item.pharmacyProductId}
              item={item}
              onRemove={() => removeItem(item.pharmacyProductId)}
            />
          ))}
        </Box>
      )}

      {/* Review & Print button */}
      <Button
        fullWidth
        variant="contained"
        disabled={itemCount === 0}
        onClick={handleReviewAndPrint}
        sx={{
          background: '#00A86B',
          borderRadius: '10px',
          py: 1.5,
          fontWeight: 700,
          fontSize: 15,
          textTransform: 'none',
          '&:hover': { background: '#007F50' },
          '&.Mui-disabled': { background: '#E1E8ED', color: '#A0AEC0' },
        }}
      >
        Revisar e Imprimir
      </Button>
    </Drawer>
  );
};
